import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, canManageMembers } from './stores/auth';
import { Shell } from './components/Shell';
import { Login, AcceptInvite } from './pages/Auth';
import { DevicesPage } from './pages/Devices';
import { PackagesPage } from './pages/Packages';
import { PlaylistsPage } from './pages/Playlists';
import { SchedulesPage } from './pages/Schedules';
import AgentReleasesPage from './pages/AgentReleases';
import { MembersPage } from './pages/Members';
import { AssetsPage } from './pages/Assets';
import { PackageEditorPage } from './pages/PackageEditor';
import { PlatformOrgsPage, PlatformUsersPage, PlatformStatsPage } from './pages/Platform';
import { PlatformMonitoringPage } from './pages/Monitoring';

// Lazy-load the device detail page so its ~400KB recharts dependency stays
// out of the main bundle. Only paid for when someone clicks a device name.
const DeviceDetailPage = lazy(() => import('./pages/Devices/DeviceDetail'));

function Protected({ children }: { children: JSX.Element }) {
  const token = useAuth((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function PlatformOnly({ children }: { children: JSX.Element }) {
  const isPlatformAdmin = useAuth((s) => s.isPlatformAdmin);
  if (!isPlatformAdmin) return <Navigate to="/devices" replace />;
  return children;
}

function ManagerOnly({ children }: { children: JSX.Element }) {
  if (!canManageMembers()) return <Navigate to="/devices" replace />;
  return children;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invite" element={<AcceptInvite />} />
        <Route element={<Protected><Shell /></Protected>}>
          <Route path="/" element={<Navigate to="/devices" replace />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route
            path="/devices/:id"
            element={
              <Suspense fallback={<div style={{ padding: 64, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}>
                <DeviceDetailPage />
              </Suspense>
            }
          />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/:id/edit" element={<PackageEditorPage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/playlists" element={<PlaylistsPage />} />
          <Route path="/schedules" element={<SchedulesPage />} />
          <Route path="/agent-releases" element={<ManagerOnly><AgentReleasesPage /></ManagerOnly>} />
          <Route path="/members" element={<ManagerOnly><MembersPage /></ManagerOnly>} />
          <Route path="/platform/orgs"  element={<PlatformOnly><PlatformOrgsPage /></PlatformOnly>} />
          <Route path="/platform/users" element={<PlatformOnly><PlatformUsersPage /></PlatformOnly>} />
          <Route path="/platform/monitoring" element={<PlatformOnly><PlatformMonitoringPage /></PlatformOnly>} />
          <Route path="/platform/stats" element={<PlatformOnly><PlatformStatsPage /></PlatformOnly>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
